import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import type {
  AnalysisJob, BudgetProposal, PlayTicketPayload, ProposalResponse, QuinielaCurrent, QuinielaTicket,
} from '@/types/quiniela'

const KEY = ['mini-app', 'quiniela', 'current']
const POLL_MS = 2500

export function isJobActive(job: AnalysisJob | null | undefined): boolean {
  return !!job && (job.status === 'pending' || job.status === 'running')
}

/** Jornada oficial vigente + último snapshot + vigencia + job en curso. Mientras hay un análisis en marcha se
 * consulta cada pocos segundos (progreso REAL procesados/total); el snapshot anterior sigue visible hasta que
 * el nuevo se publica. */
export function useQuinielaCurrent(enabled: boolean) {
  return useQuery<QuinielaCurrent>({
    queryKey: KEY,
    queryFn: async () => (await apiClient.get('/api/mini-app/quiniela/current')).data,
    enabled,
    staleTime: 20_000,
    refetchInterval: (q) => (isJobActive(q.state.data?.analysis) ? POLL_MS : false),
  })
}

export function useStartAnalysis() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ jornadaId, force }: { jornadaId: number; force: boolean }) =>
      (await apiClient.post(`/api/mini-app/quiniela/jornadas/${jornadaId}/analysis`, null, { params: { force } })).data,
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export type ProposalState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'waiting_analysis'; jobId: number | null; processed: number; total: number }
  | { phase: 'ready'; proposal: BudgetProposal; updateAvailable: boolean }
  | { phase: 'error'; message: string }

/** Créala con JA. Si todavía no hay análisis vigente la API responde 202 con el job; se sigue ese job por su id
 * y, al completar, la propuesta se pide sola (sin bloquear la Mini App). Si el análisis falla no se reintenta
 * en bucle. `acceptStale` usa el análisis actual aunque haya uno más reciente en camino. */
export function useBudgetProposal(jornadaId: number | undefined) {
  const qc = useQueryClient()
  const [state, setState] = useState<ProposalState>({ phase: 'idle' })
  const budgetRef = useRef<number | null>(null)
  const retriedRef = useRef(false)

  const request = useCallback(async (budget: number, acceptStale = false) => {
    if (!jornadaId) return
    budgetRef.current = budget
    setState({ phase: 'loading' })
    try {
      const { data } = await apiClient.post<ProposalResponse>(
        `/api/mini-app/quiniela/jornadas/${jornadaId}/proposal`, null, { params: { budget_eur: budget, accept_stale: acceptStale } },
      )
      if (data.status === 'ready') {
        retriedRef.current = false
        setState({ phase: 'ready', proposal: data.proposal, updateAvailable: data.update_available })
      } else if (data.job && retriedRef.current && data.job.status === 'failed') {
        setState({ phase: 'error', message: 'No pudimos completar el análisis. Inténtalo de nuevo en unos minutos.' })
      } else {
        setState({
          phase: 'waiting_analysis', jobId: data.job?.id ?? null,
          processed: data.job?.progress.processed ?? 0, total: data.job?.progress.total ?? 15,
        })
        qc.invalidateQueries({ queryKey: KEY })
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setState({ phase: 'error', message: detail || 'No pudimos generar la propuesta. Inténtalo de nuevo.' })
    }
  }, [jornadaId, qc])

  const waitingJobId = state.phase === 'waiting_analysis' ? state.jobId : undefined
  useEffect(() => {
    if (state.phase !== 'waiting_analysis' || !jornadaId) return
    let cancelled = false
    const tick = async () => {
      try {
        if (waitingJobId == null) { // carrera: ya no hay análisis pendiente, se pide de nuevo una vez
          if (!retriedRef.current && budgetRef.current != null) { retriedRef.current = true; void request(budgetRef.current) }
          return
        }
        const { data: job } = await apiClient.get<AnalysisJob>(`/api/mini-app/quiniela/jornadas/${jornadaId}/analysis/${waitingJobId}`)
        if (cancelled) return
        if (job.status === 'completed') {
          if (budgetRef.current != null) { retriedRef.current = true; void request(budgetRef.current) }
        } else if (job.status === 'failed') {
          setState({ phase: 'error', message: job.error || 'No pudimos completar el análisis. Inténtalo de nuevo en unos minutos.' })
        } else {
          setState({ phase: 'waiting_analysis', jobId: job.id, processed: job.progress.processed, total: job.progress.total })
        }
      } catch {
        // un fallo puntual de red no cancela la espera: el siguiente ciclo vuelve a consultar
      }
    }
    const id = setInterval(tick, POLL_MS)
    void tick()
    return () => { cancelled = true; clearInterval(id) }
  }, [state.phase, waitingJobId, jornadaId, request])

  const reset = useCallback(() => { retriedRef.current = false; setState({ phase: 'idle' }) }, [])
  return { state, request, reset }
}


/** LA JUGUÉ: registra el boleto realmente jugado. El servidor reconstruye coste/columnas/probabilidades; aquí
 * solo se indica qué boleto. Idempotente (un doble toque devuelve el mismo boleto). */
export function usePlayTicket(jornadaId: number | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PlayTicketPayload) =>
      (await apiClient.post<{ created: boolean; ticket: QuinielaTicket }>(
        `/api/mini-app/quiniela/jornadas/${jornadaId}/tickets`, payload,
      )).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['mini-app', 'quiniela', 'tickets'] })
      qc.invalidateQueries({ queryKey: ['mini-app', 'personal-bankroll'] })
    },
  })
}

/** Mis boletos de La Quiniela (separados de las apuestas normales), con seguimiento en vivo. */
export function useQuinielaTickets(enabled = true) {
  return useQuery<{ items: QuinielaTicket[]; summary: { total: number; pending: number; settled: number; total_cost_eur: number } }>({
    queryKey: ['mini-app', 'quiniela', 'tickets'],
    queryFn: async () => (await apiClient.get('/api/mini-app/quiniela/tickets')).data,
    enabled,
    staleTime: 20_000,
    refetchInterval: (q) => ((q.state.data?.summary.pending ?? 0) > 0 ? 60_000 : false),
  })
}

export function playTicketErrorMessage(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: { message?: string } | string } } })?.response?.data?.detail
  if (detail && typeof detail === 'object' && detail.message) return detail.message
  if (typeof detail === 'string') return detail
  return 'No pudimos registrar tu boleto. Inténtalo de nuevo.'
}
